import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import Grid from "./MainGrid";
import IntentsFilter from "./IntentsFilter";
import Tools from "../utils/Tools";
import { useFilter } from "../contexts/FilterContext";

function IntentView() {
  const { intentName } = useParams();
  const navigate = useNavigate();

  const [rowData, setRowData] = useState([]);
  const { filters, setFilters } = useFilter();
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortModel, setSortModel] = useState([]);
  const [queues, setQueues] = useState([]);
  const [initiationMethods, setInitiationMethods] = useState([]);

  function formatDateToYYYYMMDD(date) {
    const d = new Date(date);
    let month = "" + (d.getMonth() + 1);
    let day = "" + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  }

  const canGoBack = window.history.length > 1;

  const handleGoBack = () => {
    // Always navigate back to the intents tab instead of the contact details tab if it was navigated onto previously.
    navigate("/intents");
  };

  const handleFilterChange = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      setCurrentPage(1);
    },
    [setFilters]
  );

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleSortChange = useCallback((newSortModel) => {
    setSortModel(newSortModel);
  }, []);

  useEffect(() => {
    setLoading(true);
    const queryParams = new URLSearchParams();

    queryParams.append("minorIntent", intentName);

    // To return some more data required for filters.
    queryParams.append("isMinorIntentView", true);

    if (filters.dateRange) {
      queryParams.append("dateRange", filters.dateRange.value);
    }
    if (filters.queue && filters.queue.length > 0) {
      filters.queue.forEach(queue => {
        queryParams.append("queue", queue.value);
      });
    }
    if (filters.initiationMethod && filters.initiationMethod.length > 0) {
      filters.initiationMethod.forEach((initiationMethod) => {
        queryParams.append("initiationMethod", initiationMethod.value);
      });
    }
    if (!filters.excludeWeekends) {
      queryParams.append("excludeWeekends", filters.excludeWeekends);
    }

    if (filters.dateStart) {
      queryParams.append("dateStart", formatDateToYYYYMMDD(filters.dateStart));
    }
    if (filters.dateEnd) {
      queryParams.append("dateEnd", formatDateToYYYYMMDD(filters.dateEnd));
    }

    queryParams.append("page", currentPage);

    if (sortModel && sortModel.length > 0) {
      sortModel.forEach((sortItem) => {
        queryParams.append("sortBy", sortItem.colId);
        queryParams.append("sortOrder", sortItem.sort);
      });
    }

    Tools.sendRequest("get-data", queryParams.toString()).then((response) => {
      console.log("DEBUG - GOT DATA", response);
      setRowData(response.data);
      setTotalRecords(response.totalRecords);
      setTotalPages(response.totalPages);
      setCurrentPage(response.currentPage);
      setQueues(
        Object.keys(response.queueCounts)
          .map((a) => ({
            value: a,
            label: a,
            count: response.queueCounts[a],
          }))
          .sort((a, b) => b.count - a.count) // Sort descending by count
      );
      setInitiationMethods(
        Object.keys(response.initiationMethodCounts)
          .map((a) => ({
            value: a,
            label: a,
            count: response.initiationMethodCounts[a],
          }))
          .sort((a, b) => b.count - a.count) // Sort descending by count
      );
      setLoading(false); // Data fetching is complete
    });
  }, [currentPage, filters, intentName, sortModel]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4 max-w-screen-xl mx-auto">
        <div className="w-1/3">
          {canGoBack && (
            <button
              onClick={handleGoBack}
              className="text-sm text-gray-600 hover:text-gray-800 flex items-center"
            >
              <FaArrowLeft className="mr-1" /> Back
            </button>
          )}
        </div>
        <h2 className="text-2xl font-bold text-center w-1/3">{intentName}</h2>
        <div className="w-1/3 flex justify-end mr-10"></div>
      </div>

      <div className="container mx-auto p-4">
        <IntentsFilter
          filters={filters}
          setFilters={handleFilterChange}
          queues={queues}
          initiationMethods={initiationMethods}
          miniView={true}
        />
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="loader"></div>
          </div>
        ) : (
          <>
            <Grid
              data={rowData}
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecs={totalRecords}
              onPageChange={handlePageChange}
              onSortChange={handleSortChange}
              filters={filters}
              sortModel={sortModel} // Pass sortModel to Grid
            />
          </>
        )}
      </div>
    </div>
  );
}

export default IntentView;
